/* eslint-disable import/no-cycle */
import {
  WidgetTypeConfig,
  ConfigurationPanelStructureItemEnum,
  ComponentNameEnum,
  ElementPartTypeEnum,
  StepperSignEnum,
  TemplateStructureEnum,
} from '@/types';
import getHorizontalSpacesFieldConfig from '@/components/template-builder/config/fields/horizontal-spaces-config';
import getVerticalSpacesFieldConfig from '@/components/template-builder/config/fields/vertical-spaces-config';
import getColorFieldConfig from '@/components/template-builder/config/fields/color-config';
import getAllBorderRadiusFieldConfig from '@/components/template-builder/config/fields/all-border-radius-config';
import mediaDisplayFieldConfig from '@/components/template-builder/config/fields/media-display-config';
import getWidgetCommonStylesGroup from '@/components/template-builder/config/fields/widget-common-styles-config';
import fontSizeFieldConfig from '@/components/template-builder/config/fields/font-size-config';
import getStepperFieldConfig from '@/components/template-builder/config/fields/stepper-config';
import getInputVariablesFieldConfig from '@/components/template-builder/config/fields/input-variables-config';
import { getTimezoneOptions } from '@/components/template-builder/config/fields/timezone-options';
import getCountdownDateVariableOptions from '@/components/template-builder/config/fields/countdown-date-variables';
import {
  setCssProperty,
  setCssPropertyAndRemoveIfZero,
  setMediaDisplay,
  setContent,
  setAttribute,
  setCountdownEndDate,
  setCountdownRelativeDate,
  setCountdownDateVariable,
  setCountdownTimezone,
  setCountdownShowDays,
  setCountdownShowHours,
  setCountdownShowMinutes,
  setCountdownShowSeconds,
} from '@/components/template-builder/setters';
import {
  getClassByName,
  getCssPropertyByName,
  getAttributeByName,
  getContent,
} from '@/components/template-builder/getters';
import getConditionsAndRepetitionGroup from '@/components/template-builder/config/fields/conditions-repetition-group-helper';

const displayWidgetCountdownConfigV4: WidgetTypeConfig = {
    tabPanel: {
      label: 'templateBuilder.widgetsHeaders.countdown',
      items: [
        {
          type: ConfigurationPanelStructureItemEnum.TAB_ITEM,
          tabItemType: TemplateStructureEnum.GROUP,
          label: 'templateBuilder.configs.widgetConfiguration',
          tabPanel: {
            items: [
              {
                type: ConfigurationPanelStructureItemEnum.FIELDS_GROUP,
                label: 'templateBuilder.configs.countdownSettings',
                selector: 'body #spm_body #{ID}',
                items: [
                  {
                    label: 'templateBuilder.fields.countdownDateType',
                    selector: '{SELECTOR}',
                    fieldId: 'countdownDateType',
                    properties: [{
                      name: 'data-date-type',
                      getter: getAttributeByName,
                      setters: [setCountdownRelativeDate],
                      refreshWidgetFields: true,
                    }],
                    componentName: ComponentNameEnum.DROPDOWN,
                    options: {
                      defaultValue: 'relative',
                      list: [
                        { value: 'fixed', label: 'templateBuilder.fields.countdownDateTypeFixed' },
                        { value: 'relative', label: 'templateBuilder.fields.countdownDateTypeRelative' },
                        { value: 'variable', label: 'templateBuilder.fields.countdownDateTypeVariable' },
                      ],
                    },
                    displayToggle: false,
                  },
                  {
                    label: 'templateBuilder.fields.countdownEndDate',
                    selector: '{SELECTOR}',
                    parentFieldId: 'countdownDateType',
                    isVisible: (items: any[], parentFieldId: string) => {
                      const property = items.find((item) => item.fieldId === parentFieldId);
                      if (property && Array.isArray(property.properties) && property.properties.length) {
                        return property.properties[0].value === 'fixed';
                      }
                      return true;
                    },
                    properties: [{
                      name: 'data-end-date',
                      getter: getAttributeByName,
                      setters: [setCountdownEndDate],
                    }],
                    componentName: ComponentNameEnum.DATETIME_PICKER,
                    options: {
                      defaultValue: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
                    },
                    displayToggle: false,
                  },
                  {
                    label: 'templateBuilder.fields.countdownTimezone',
                    selector: '{SELECTOR}',
                    parentFieldId: 'countdownDateType',
                    isVisible: (items: any[], parentFieldId: string) => {
                      const property = items.find((item) => item.fieldId === parentFieldId);
                      if (property && Array.isArray(property.properties) && property.properties.length) {
                        return property.properties[0].value === 'fixed';
                      }
                      return true;
                    },
                    properties: [{
                      name: 'data-timezone',
                      getter: getAttributeByName,
                      setters: [setCountdownTimezone],
                    }],
                    componentName: ComponentNameEnum.DROPDOWN,
                    options: {
                      defaultValue: 'UTC',
                      list: getTimezoneOptions(),
                      filter: true,
                      optionGroupLabel: 'label',
                      optionGroupChildren: 'items',
                    },
                    displayToggle: false,
                  },
                  {
                    label: 'templateBuilder.fields.countdownDateVariable',
                    selector: '{SELECTOR}',
                    parentFieldId: 'countdownDateType',
                    isVisible: (items: any[], parentFieldId: string) => {
                      const property = items.find((item) => item.fieldId === parentFieldId);
                      if (property && Array.isArray(property.properties) && property.properties.length) {
                        return property.properties[0].value === 'variable';
                      }
                      return false;
                    },
                    properties: [{
                      name: 'data-date-variable',
                      getter: getAttributeByName,
                      setters: [setCountdownDateVariable],
                    }],
                    componentName: ComponentNameEnum.DROPDOWN,
                    options: {
                      defaultValue: 'voucher.date_end',
                      list: getCountdownDateVariableOptions(),
                    },
                    displayToggle: false,
                  },
                  {
                    label: 'templateBuilder.fields.countdownRelativeDays',
                    selector: '{SELECTOR}',
                    parentFieldId: 'countdownDateType',
                    isVisible: (items: any[], parentFieldId: string) => {
                      const property = items.find((item) => item.fieldId === parentFieldId);
                      if (property && Array.isArray(property.properties) && property.properties.length) {
                        return property.properties[0].value === 'relative';
                      }
                      return false;
                    },
                    properties: [{
                      name: 'data-relative-days',
                      getter: getAttributeByName,
                      setters: [setCountdownRelativeDate],
                    }],
                    ...getStepperFieldConfig(StepperSignEnum.NONE, 1, 0, 365),
                    options: {
                      ...getStepperFieldConfig(StepperSignEnum.NONE, 1, 0, 365).options,
                      defaultValue: 1,
                    },
                    displayToggle: false,
                  },
                  {
                    label: 'templateBuilder.fields.countdownRelativeHours',
                    selector: '{SELECTOR}',
                    parentFieldId: 'countdownDateType',
                    isVisible: (items: any[], parentFieldId: string) => {
                      const property = items.find((item) => item.fieldId === parentFieldId);
                      if (property && Array.isArray(property.properties) && property.properties.length) {
                        return property.properties[0].value === 'relative';
                      }
                      return false;
                    },
                    properties: [{
                      name: 'data-relative-hours',
                      getter: getAttributeByName,
                      setters: [setCountdownRelativeDate],
                    }],
                    ...getStepperFieldConfig(StepperSignEnum.NONE, 1, 0, 23),
                    displayToggle: false,
                  },
                  {
                    label: 'templateBuilder.fields.countdownRelativeMinutes',
                    selector: '{SELECTOR}',
                    parentFieldId: 'countdownDateType',
                    isVisible: (items: any[], parentFieldId: string) => {
                      const property = items.find((item) => item.fieldId === parentFieldId);
                      if (property && Array.isArray(property.properties) && property.properties.length) {
                        return property.properties[0].value === 'relative';
                      }
                      return false;
                    },
                    properties: [{
                      name: 'data-relative-minutes',
                      getter: getAttributeByName,
                      setters: [setCountdownRelativeDate],
                    }],
                    ...getStepperFieldConfig(StepperSignEnum.NONE, 1, 0, 59),
                    displayToggle: false,
                  },
                  {
                    label: 'templateBuilder.fields.countdownRelativeSeconds',
                    selector: '{SELECTOR}',
                    parentFieldId: 'countdownDateType',
                    isVisible: (items: any[], parentFieldId: string) => {
                      const property = items.find((item) => item.fieldId === parentFieldId);
                      if (property && Array.isArray(property.properties) && property.properties.length) {
                        return property.properties[0].value === 'relative';
                      }
                      return false;
                    },
                    properties: [{
                      name: 'data-relative-seconds',
                      getter: getAttributeByName,
                      setters: [setCountdownRelativeDate],
                    }],
                    ...getStepperFieldConfig(StepperSignEnum.NONE, 1, 0, 59),
                    displayToggle: false,
                  },
                  {
                    label: 'templateBuilder.fields.countdownShowDays',
                    selector: '{SELECTOR}',
                    properties: [{
                      name: 'data-show-days',
                      getter: getAttributeByName,
                      setters: [setCountdownShowDays],
                    }],
                    componentName: ComponentNameEnum.CHECKBOX,
                    options: {
                      defaultValue: true,
                    },
                  },
                  {
                    label: 'templateBuilder.fields.countdownShowHours',
                    selector: '{SELECTOR}',
                    properties: [{
                      name: 'data-show-hours',
                      getter: getAttributeByName,
                      setters: [setCountdownShowHours],
                    }],
                    componentName: ComponentNameEnum.CHECKBOX,
                    options: {
                      defaultValue: true,
                    },
                  },
                  {
                    label: 'templateBuilder.fields.countdownShowMinutes',
                    selector: '{SELECTOR}',
                    properties: [{
                      name: 'data-show-minutes',
                      getter: getAttributeByName,
                      setters: [setCountdownShowMinutes],
                    }],
                    componentName: ComponentNameEnum.CHECKBOX,
                    options: {
                      defaultValue: true,
                    },
                  },
                  {
                    label: 'templateBuilder.fields.countdownShowSeconds',
                    selector: '{SELECTOR}',
                    properties: [{
                      name: 'data-show-seconds',
                      getter: getAttributeByName,
                      setters: [setCountdownShowSeconds],
                    }],
                    componentName: ComponentNameEnum.CHECKBOX,
                    options: {
                      defaultValue: true,
                    },
                  },
                ],
              },
              {
                type: ConfigurationPanelStructureItemEnum.FIELDS_GROUP,
                label: 'templateBuilder.configs.countdownLabels',
                selector: 'body #spm_body #{ID}',
                items: [
                  {
                    label: 'templateBuilder.fields.countdownLabelDays',
                    selector: '{SELECTOR} .spm_countdown_days .spm_countdown_label',
                    properties: [{ name: 'content', getter: getContent, setters: [setContent] }],
                    ...getInputVariablesFieldConfig('Jours', [], false, false),
                  },
                  {
                    label: 'templateBuilder.fields.countdownLabelHours',
                    selector: '{SELECTOR} .spm_countdown_hours .spm_countdown_label',
                    properties: [{ name: 'content', getter: getContent, setters: [setContent] }],
                    ...getInputVariablesFieldConfig('Heures', [], false, false),
                  },
                  {
                    label: 'templateBuilder.fields.countdownLabelMinutes',
                    selector: '{SELECTOR} .spm_countdown_minutes .spm_countdown_label',
                    properties: [{ name: 'content', getter: getContent, setters: [setContent] }],
                    ...getInputVariablesFieldConfig('Minutes', [], false, false),
                  },
                  {
                    label: 'templateBuilder.fields.countdownLabelSeconds',
                    selector: '{SELECTOR} .spm_countdown_seconds .spm_countdown_label',
                    properties: [{ name: 'content', getter: getContent, setters: [setContent] }],
                    ...getInputVariablesFieldConfig('Secondes', [], false, false),
                  },
                ],
              },
            ],
          },
        },
        {
          type: ConfigurationPanelStructureItemEnum.TAB_ITEM,
          tabItemType: TemplateStructureEnum.GROUP,
          label: 'templateBuilder.configs.widgetStyles',
          tabPanel: {
            items: [
              {
                type: ConfigurationPanelStructureItemEnum.FIELDS_GROUP,
                label: 'templateBuilder.configs.countdownItemStyles',
                selector: 'body #spm_body #{ID}',
                canSave: true,
                elementPartType: ElementPartTypeEnum.STYLES,
                items: [
                  {
                    label: 'templateBuilder.fields.countdownItemBackground',
                    selector: '{SELECTOR} .spm_countdown_item',
                    properties: [{
                      name: 'background',
                      getter: getCssPropertyByName,
                      setters: [setCssProperty],
                    }],
                    ...getColorFieldConfig('#1a1a1a', false, true),
                  },
                  {
                    label: 'templateBuilder.fields.countdownValueColor',
                    selector: '{SELECTOR} .spm_countdown_value',
                    properties: [{
                      name: 'color',
                      getter: getCssPropertyByName,
                      setters: [setCssProperty],
                    }],
                    ...getColorFieldConfig('#ffffff', true, false),
                  },
                  {
                    label: 'templateBuilder.fields.countdownValueSize',
                    selector: '{SELECTOR} .spm_countdown_value',
                    properties: [{
                      name: 'font-size',
                      getter: getCssPropertyByName,
                      setters: [setCssProperty],
                    }],
                    ...fontSizeFieldConfig,
                  },
                  {
                    label: 'templateBuilder.fields.countdownLabelColor',
                    selector: '{SELECTOR} .spm_countdown_label',
                    properties: [{
                      name: 'color',
                      getter: getCssPropertyByName,
                      setters: [setCssProperty],
                    }],
                    ...getColorFieldConfig('#ffffff', true, false),
                  },
                  {
                    label: 'templateBuilder.fields.countdownLabelSize',
                    selector: '{SELECTOR} .spm_countdown_label',
                    properties: [{
                      name: 'font-size',
                      getter: getCssPropertyByName,
                      setters: [setCssProperty],
                    }],
                    ...fontSizeFieldConfig,
                  },
                  {
                    label: 'templateBuilder.fields.contours',
                    selector: '{SELECTOR} .spm_countdown_item',
                    properties: [
                      {
                        name: 'border-top-left-radius',
                        getter: getCssPropertyByName,
                        setters: [setCssPropertyAndRemoveIfZero],
                      },
                      {
                        name: 'border-top-right-radius',
                        getter: getCssPropertyByName,
                        setters: [setCssPropertyAndRemoveIfZero],
                      },
                      {
                        name: 'border-bottom-left-radius',
                        getter: getCssPropertyByName,
                        setters: [setCssPropertyAndRemoveIfZero],
                      },
                      {
                        name: 'border-bottom-right-radius',
                        getter: getCssPropertyByName,
                        setters: [setCssPropertyAndRemoveIfZero],
                      },
                    ],
                    componentName: ComponentNameEnum.SPACES,
                    options: {
                      ...getAllBorderRadiusFieldConfig('border-radius', 8).options,
                    },
                  },
                  {
                    label: 'templateBuilder.fields.countdownItemGap',
                    selector: '{SELECTOR} .spm_countdown_container',
                    properties: [{
                      name: 'gap',
                      getter: getCssPropertyByName,
                      setters: [setCssProperty],
                    }],
                    ...getStepperFieldConfig(StepperSignEnum.PX, 1, 0, 50),
                  },
                  {
                    label: 'templateBuilder.fields.countdownItemPadding',
                    selector: '{SELECTOR} .spm_countdown_item',
                    properties: [
                      { name: 'padding-top', getter: getCssPropertyByName, setters: [setCssPropertyAndRemoveIfZero] },
                      { name: 'padding-bottom', getter: getCssPropertyByName, setters: [setCssPropertyAndRemoveIfZero] },
                      { name: 'padding-left', getter: getCssPropertyByName, setters: [setCssPropertyAndRemoveIfZero] },
                      { name: 'padding-right', getter: getCssPropertyByName, setters: [setCssPropertyAndRemoveIfZero] },
                    ],
                    componentName: ComponentNameEnum.SPACES,
                    options: {
                      ...getVerticalSpacesFieldConfig('padding').options,
                      ...getHorizontalSpacesFieldConfig('padding').options,
                    },
                  },
                ],
              },
              getWidgetCommonStylesGroup(),
            ],
          },
        },
        getConditionsAndRepetitionGroup('body #spm_body #{ID}'),
      ],
    },
};

export default displayWidgetCountdownConfigV4;
